import React, { useState, useEffect } from "react";
import { formatNumberWithCommas, uncomma } from "@/utils";
import {
    selfCarLimitTypeOptions,
    selfCarIndemnityOptions,
    selfCarIndemnityOptionsForOrderCall,
} from "@/constants/selectOptions";

export const InsuranceInfoCar = ({ contractInsuranceCondition, onUpdate, contractIndex, disabled = false, showToast, isOrderCall = false }) => {
    const [indemnityInputMode, setIndemnityInputMode] = useState(false);
    const [limitAmountInputMode, setLimitAmountInputMode] = useState(false);

    // 자차한도 타입 변경 핸들러
    const handleLimitTypeChange = (type) => {
        if (!disabled) {
            onUpdate("selfCarLimitType", type);
            if (type === "PRICE") {
                onUpdate("selfCarLimitValue", 30000000); // 기본값 3천만원
            } else {
                onUpdate("selfCarLimitValue", 0); // 비율 기본값 0%
            }
        }
    };

    // 자차면책금 라디오 변경 핸들러
    const handleIndemnityRadioChange = (value) => {
        if (!disabled) {
            onUpdate("selfCarIndemnityPrice", value);
            setIndemnityInputMode(false);
        }
    };

    // 자차면책금 직접입력 핸들러
    const handleIndemnityInputChange = (e) => {
        if (!disabled) {
            const value = e.target.value;
            if (value) {
                const numericValue = parseInt(uncomma(value));
                // 10억 미만으로 제한 (100,000만원)
                if (numericValue >= 100000) {
                    if (showToast) {
                        showToast("자차면책금이 너무 큽니다. 10억원 미만으로 입력해주세요.");
                    }
                    return;
                }
                onUpdate("selfCarIndemnityPrice", numericValue * 10000); // 만원 단위를 원 단위로 변환
            } else {
                onUpdate("selfCarIndemnityPrice", null);
            }
        }
    };

    // 자차한도(금액) 직접입력 핸들러
    const handleLimitAmountChange = (e) => {
        if (!disabled) {
            const value = e.target.value;
            if (value) {
                const numericValue = parseInt(uncomma(value));
                // 10억 미만으로 제한 (100,000만원)
                if (numericValue >= 100000) {
                    if (showToast) {
                        showToast("자차한도(금액)가 너무 큽니다. 10억원 미만으로 입력해주세요.");
                    }
                    return;
                }
                onUpdate("selfCarLimitValue", numericValue * 10000); // 만원 단위를 원 단위로 변환
            } else {
                onUpdate("selfCarLimitValue", null);
            }
        }
    };

    // 자차한도(비율) 직접입력 핸들러
    const handleLimitPercentChange = (e) => {
        if (!disabled) {
            // 0~100 정수만 허용
            let value = e.target.value.replace(/[^0-9]/g, '');
            if (value !== '') {
                const numValue = parseInt(value);
                if (numValue > 100) {
                    value = '100';
                }
            }
            e.target.value = value;
            onUpdate("selfCarLimitValue", value ? parseInt(value) : 0);
        }
    };

    // 표시용 값 변환 (원 단위를 만원 단위로)
    const getDisplayValue = (value) => {
        if (value === null) return "";
        return value ? (value / 10000).toLocaleString() : "";
    };

    // 자차면책금이 선택지에 없는 값인지 확인
    const isIndemnityCustomValue = (value) => {
        if (value === null) return false;
        const options = isOrderCall ? selfCarIndemnityOptionsForOrderCall : selfCarIndemnityOptions;
        return !options.some(option => option.value === value);
    };

    // 자차한도(금액)이 선택지에 없는 값인지 확인 (필요시 추가)
    const isLimitAmountCustomValue = (value) => {
        // 현재는 항상 직접 입력이므로 false
        return false;
    };

    // 컴포넌트 마운트 시 초기 상태 설정
    useEffect(() => {
        if (contractInsuranceCondition?.selfCarIndemnityPrice) {
            setIndemnityInputMode(isIndemnityCustomValue(contractInsuranceCondition.selfCarIndemnityPrice));
        }
    }, [contractInsuranceCondition?.selfCarIndemnityPrice]);

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>자차면책금</span>
                </div>
                <div className="row-content">
                    {disabled ? (
                        <div className="form-element">
                            <input
                                type="text"
                                placeholder="금액 입력"
                                value={getDisplayValue(contractInsuranceCondition?.selfCarIndemnityPrice)}
                                readOnly
                                disabled={true}
                            />
                            <span className="input-esset">만원</span>
                        </div>
                    ) : (
                        <>
                            <div className="form-element">
                                {indemnityInputMode ? (
                                    <>
                                        <input
                                            type="text"
                                            placeholder="금액 입력"
                                            value={getDisplayValue(contractInsuranceCondition?.selfCarIndemnityPrice)}
                                            onChange={handleIndemnityInputChange}
                                            onInput={formatNumberWithCommas}
                                        />
                                        <span className="input-esset">만원</span>
                                    </>
                                ) :
                                    <>
                                        {(isOrderCall ? selfCarIndemnityOptionsForOrderCall : selfCarIndemnityOptions).map((option) => (
                                            <div key={option.value || "none"} className="radiobox">
                                                <input
                                                    type="radio"
                                                    name={`selfCarIndemnity_${contractIndex}`}
                                                    id={`selfCarIndemnity_${option.value || "none"}_${contractIndex}`}
                                                    checked={!indemnityInputMode && contractInsuranceCondition?.selfCarIndemnityPrice === option.value}
                                                    onChange={() => handleIndemnityRadioChange(option.value)}
                                                />
                                                <label htmlFor={`selfCarIndemnity_${option.value || "none"}_${contractIndex}`}>
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>
                            <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => setIndemnityInputMode(!indemnityInputMode)}
                                style={{ flexShrink: 0 }}
                            >
                                {indemnityInputMode ? "선택지로" : "직접입력"}
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>자차한도 구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {selfCarLimitTypeOptions.map((option) => (
                            <div key={option.value} className="radiobox">
                                <input
                                    type="radio"
                                    name={`selfCarLimitType_${contractIndex}`}
                                    id={`selfCarLimitType_${option.value}_${contractIndex}`}
                                    checked={contractInsuranceCondition?.selfCarLimitType === option.value}
                                    onChange={() => handleLimitTypeChange(option.value)}
                                    disabled={disabled}
                                />
                                <label htmlFor={`selfCarLimitType_${option.value}_${contractIndex}`}>
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            {contractInsuranceCondition?.selfCarLimitType === "PRICE" && (
                <>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>자차한도(금액)</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    placeholder="금액 입력"
                                    value={getDisplayValue(contractInsuranceCondition?.selfCarLimitValue)}
                                    onChange={handleLimitAmountChange}
                                    onInput={disabled ? undefined : formatNumberWithCommas}
                                    disabled={disabled}
                                />
                                <span className="input-esset">만원</span>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                </>
            )}

            {contractInsuranceCondition?.selfCarLimitType === "PERCENT" && (
                <>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>자차한도(비율)</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    min="0"
                                    max="100"
                                    placeholder="0"
                                    value={contractInsuranceCondition?.selfCarLimitValue || ""}
                                    onChange={handleLimitPercentChange}
                                    disabled={disabled}
                                />
                                <span className="input-esset">%</span>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                </>
            )}
        </>
    );
};
